/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef, useMemo } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useOutletContext,
  useNavigate
} from 'react-router-dom'
import { Loading, ControlButtons } from '@renderer/components'
import ScaleToFit from '@renderer/components/ScaleToFit'
import { SettingsModal } from '@renderer/components/view'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { usePrintStore } from '@renderer/stores/printStore'
import cogoToast from '@successtar/cogo-toast'
import { getBrandLogoPath, getBrandProductName } from '@renderer/utils/brandUtils'
import { getCustomRoutes } from './view/customRoutes'
import RecordPage from './view/Record'
import PostSessionAIPage from './view/PostSessionAI'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { DeviceType } from '@renderer/types/utils'
import { CameraPrompt } from '@renderer/components/view/CameraPrompt'
import { FaExternalLinkAlt, FaTimes } from 'react-icons/fa'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { useTranslation } from 'react-i18next'
import { UpgradeModal } from '@renderer/components/UpgradeModal'
import { useUploadStore } from '@renderer/stores/uploadStore'
import { createContext, useContext } from 'react'
import { useDeviceManager } from '@renderer/hooks'
import { useDeviceOrientation } from '@renderer/hooks/useDeviceOrientation'
import { ResponsiveTierProvider } from '@renderer/context/ResponsiveTierContext'
import type { DesignOrientation } from '@renderer/types/configState'
import { getDesignCanvasSizeForOrientation } from '@renderer/utils/designCanvasSize'
import { CameraWarningModal } from '@renderer/components/view/CameraWarningModal'
import { Screensaver } from '@renderer/components/Screensaver'
import { useIdleStore } from '@renderer/stores/idleStore'
import {
  openExternalUrl,
  ensureCameraPermission,
  getTunnelStatus,
  hasElectronPreload,
  setIdleState,
} from '@renderer/lib/runtime'
import {
  getStoredProjectEffectiveUpdatedAt,
  setStoredProjectEffectiveUpdatedAt
} from '@renderer/utils/projectVersionCache'

/**
 * True when the booth may operate: valid device subscription, or a positive credit balance.
 * Stale "Unlimited" with invalid sub must not count as access (avoids Number('Unlimited') => NaN).
 */
function hasUsableBoothAccess(
  isSubscriptionValid: boolean,
  credits: number | string
): boolean {
  if (isSubscriptionValid) return true
  if (typeof credits === 'number') return credits > 0
  if (credits === 'Unlimited') return false
  const n = Number(credits)
  if (Number.isNaN(n)) return false
  return n > 0
}

// Context for camera recovery
export interface CameraRecoveryContextType {
  onCameraRecoveryRequest: (onReconnected: () => void) => void
}

export const CameraRecoveryContext = createContext<CameraRecoveryContextType | null>(null)

export const useCameraRecovery = (): CameraRecoveryContextType | null => {
  return useContext(CameraRecoveryContext)
}

const NoCreditsModal: React.FC<{
  onClose: () => void
  setShowUpgradeModal: (show: boolean) => void
}> = ({ onClose, setShowUpgradeModal }) => {
  const { setDefaultProjectId } = useSettingsStore()
  const { t } = useTranslation()
  const isKlakKlikKluk = window.location.hostname.includes('klakklikkluk')

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold mb-4">{t('view.noCreditsModal.title')}</h2>
        <p className="text-gray-600 mb-6">{t('view.noCreditsModal.description')}</p>
        <div className="flex flex-col gap-3">
          <button
            onClick={() => {
              setShowUpgradeModal(true)
            }}
            className="w-full flex justify-center bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
          >
            <span className="flex items-center gap-4">{t('projectNav.activateSubscription')}</span>
          </button>
          {!isKlakKlikKluk && (
            <button
              onClick={() => {
                setDefaultProjectId(undefined)
                void openExternalUrl(`${import.meta.env.VITE_API_URL}/dashboard/projects`)
              }}
              className="w-full flex justify-center bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors"
            >
              <span className="flex items-center gap-4">
                {t('view.noCreditsModal.topUpCredits')} <FaExternalLinkAlt />
              </span>
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full border border-gray-300 py-2 px-4 rounded hover:bg-gray-100 transition-colors"
          >
            {t('view.noCreditsModal.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

const PoweredByWatermark: React.FC<{ customPath: string }> = ({ customPath }) => {
  const show =
    customPath === 'booth-daughter' ||
    (customPath !== 'bahela' &&
      customPath !== 'bahelaevent' &&
      customPath !== 'bahela-quick' &&
      customPath !== 'bahelastruk' &&
      !window.location.hostname.includes('klakklikkluk') &&
      !['starter', 'protrial', 'professional', 'enterprise'].includes(
        useCreditsStore.getState().package
      ))
  if (!show) return null
  return (
    <div className="pointer-events-none absolute bottom-2 right-2 z-[90] flex origin-bottom-right items-center gap-1.5 opacity-70">
      <span className="text-xs text-gray-600">Powered By</span>
      <img src={getBrandLogoPath()} alt={getBrandProductName()} className="h-5" />
    </div>
  )
}

// Wrapper component to handle redirections
const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { projectId } = useParams()
  const location = useLocation()
  const { config, getPageSettings } = useConfigStore()
  const welcomeSettings = getPageSettings('welcome')
  const currentPage = location.pathname.split('/').pop() || 'welcome'
  const { id: sessionId } = useSessionStateStore()
  const customPath = welcomeSettings?.customPath

  // If no session ID exists and not on tutorial page, redirect to welcome page
  if (!sessionId && currentPage !== 'tutorial') {
    return <Navigate to={`/${projectId}`} replace />
  }

  // Get the next page based on current page and settings
  const getNextPage = (): string | null => {
    const pages = config?.pages
    const { mode } = useSessionStateStore.getState()
    if (!pages) return null

    // If using custom path, don't handle automatic page flow
    if (customPath) return null

    switch (currentPage) {
      case 'filter':
        if (!pages.filter?.settings?.enabled) {
          return 'frame'
        }
        break
      case 'frame':
        if (!pages.frame?.settings?.enabled) {
          return welcomeSettings?.startWithPayment ? 'capture' : 'checkout'
        }
        break
      case 'checkout':
        if (!pages.checkout?.settings?.enabled) {
          return 'payment'
        }
        break
      case 'payment':
        if (!pages.payment?.settings?.enabled) {
          if (welcomeSettings?.startWithPayment) {
            const filterSettings = pages.filter?.settings
            const applyAfterCapture = filterSettings?.applyAfterCapture || false
            return applyAfterCapture ? 'frame' : 'filter'
          }
          return mode === 'capture' ? 'capture' : 'record'
        }
        break
      case 'capture':
        if (
          mode === 'record' ||
          pages.capture?.settings?.enabled === false ||
          pages.capture?.settings?.enabled === null
        ) {
          return mode === 'record' ? 'record' : 'retake'
        }
        break
      case 'retake':
        if (!pages.retake?.settings?.enabled || mode === 'record') {
          return mode === 'record' ? 'result' : 'record'
        }
        // Don't auto-redirect from retake page - let the page handle navigation via Continue button
        // The retake page will navigate based on frame-based mode and other settings
        break
      case 'record':
        if (!pages.record?.settings?.enabled) {
          return mode === 'record' ? 'result' : 'select'
        }
        break
      case 'select':
        // Don't auto-redirect from select page - let the page handle navigation via Continue button
        // The select page will navigate to edit (if enabled) or result based on settings
        break
      case 'edit':
        // Check if Edit page is disabled - only redirect if disabled
        if (!pages.edit?.settings?.enabled) {
          return 'result'
        }
        // Don't auto-redirect from edit page - let the page handle navigation via Continue button
        // The edit page will navigate to result when user clicks Continue
        break
      case 'post-session-ai':
        break
    }
    return null
  }

  const nextPage = getNextPage()
  if (nextPage) {
    return <Navigate to={`/${projectId}/${nextPage}`} replace />
  }

  return <>{children}</>
}

const View: React.FC = () => {
  const { projectId } = useParams()
  const location = useLocation()
  const { user } = useOutletContext<{ user: { _id: string; email: string; username: string } }>()
  const navigate = useNavigate()
  const {
    setDefaultProjectId,
    ejectCameraWhenIdle,
    showCameraQualityWarnings,
    enableScreensaver,
    screensaverTimeout
  } = useSettingsStore()
  // Group all useState hooks together
  const [showSettings, setShowSettings] = useState(false)
  const [isFromGearButton, setIsFromGearButton] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [tunnelUrl, setTunnelUrl] = useState<string | null>(null)
  const [isTunnelActive, setIsTunnelActive] = useState<boolean>(false)
  const [showCameraPrompt, setShowCameraPrompt] = useState(false)
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false)
  const noCreditsModalTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [initialCreditsLoaded, setInitialCreditsLoaded] = useState(false)
  const [showCameraWarning, setShowCameraWarning] = useState(false)
  const [cameraQualityIssue, setCameraQualityIssue] = useState<'dark' | 'black' | 'noise' | 'none'>(
    'none'
  )
  const [isCameraWarningIgnored, setIsCameraWarningIgnored] = useState(false)
  const cameraRecoveryCallbackRef = useRef<(() => void) | null>(null)
  const reconnectFailureCount = useRef(0)
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null)
  const screensaverTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [dismissedLoadingIndicator, setDismissedLoadingIndicator] = useState(false)
  const [dismissedUploadIndicator, setDismissedUploadIndicator] = useState(false)

  // Store hooks
  const { setIdle, setScreensaverActive, wakeUp, isScreensaverActive } = useIdleStore()
  const { initializeSession, logActivity, sessionUploadError, setSessionUploadError, refreshProjectCatalog } =
    useSessionStateStore()
  const sessionId = useSessionStateStore((s) => s.id)
  const { config, getPageSettings, loadingProgress, setConfig } = useConfigStore()
  const { progress: uploadProgress } = useUploadStore()
  const { status: printStatus, error: printError, settings, selectedPrinter, isPrintingEnabled } =
    usePrintStore()
  const { credits, fetchSubscriptionAndCredits } = useCreditsStore()
  const { isValid: isSubscriptionValid, validationError } = useSubscriptionStore()
  const { t } = useTranslation()

  // Refs
  const lastClickRef = useRef<{
    element: HTMLElement
    timestamp: Date
  } | null>(null)
  const initialDeviceValidationDone = useRef(false)

  // Get settings after store hooks
  const welcomeSettings = getPageSettings('welcome')
  const customPath = welcomeSettings?.customPath || undefined // Convert null to undefined
  const { selectedCamera } = useSettingsStore()
  // Subscribe to camera store to trigger idle timer reset when camera reconnects
  const { camera, webcam } = useCameraStore()

  // Device manager at View level so we can validate stored devices and pass to SettingsModal
  const {
    devices,
    isPreviewAvailable,
    setIsPreviewAvailable,
    localTempCamera,
    setLocalTempCamera,
    cameraConnectionRef,
    fetchDevices
  } = useDeviceManager()

  // Get page components based on customPath
  const pageComponents = useMemo(() => getCustomRoutes(customPath), [customPath])

  const isRootPath = useMemo(
    () => location.pathname.endsWith('/welcome') || location.pathname.endsWith(`/${projectId}`),
    [location.pathname, projectId]
  )

  const useScaleToFitShell = Boolean(customPath)
  const { isPortrait: deviceIsPortrait } = useDeviceOrientation()
  const designOrientation: DesignOrientation = deviceIsPortrait ? 'portrait' : 'landscape'
  /**
   * Custom-path sessions (incl. bahela) use a fixed design canvas. Match the device viewport
   * orientation to `screenSizes` / oriented layouts — not only `screenSize` (dashboard may
   * persist portrait while `screenSizes.landscape` is correct).
   */
  const scaleToFitScreenSize = useMemo(() => {
    if (!useScaleToFitShell || !config) return null
    return getDesignCanvasSizeForOrientation(config, designOrientation)
  }, [useScaleToFitShell, config, designOrientation])
  const scaleToFitVirtualViewport = useMemo(
    () =>
      scaleToFitScreenSize
        ? { width: scaleToFitScreenSize.width, height: scaleToFitScreenSize.height }
        : undefined,
    [scaleToFitScreenSize]
  )

  // Between sessions: re-fetch project + catalog when server version is newer than last sync.
  useEffect(() => {
    if (!projectId || !user || !isRootPath || sessionId) {
      return
    }

    const ac = new AbortController()

    const run = async (): Promise<void> => {
      try {
        const versionRes = await fetch(
          `${process.env.VITE_API_URL}/api/projects/version?id=${projectId}`,
          {
            credentials: 'include',
            cache: 'no-cache',
            signal: ac.signal
          }
        )
        if (!versionRes.ok || ac.signal.aborted) {
          return
        }
        const { effectiveUpdatedAt } = (await versionRes.json()) as {
          effectiveUpdatedAt: string
        }
        if (ac.signal.aborted) {
          return
        }

        const stored = getStoredProjectEffectiveUpdatedAt(projectId)
        if (stored === effectiveUpdatedAt) {
          return
        }

        const projRes = await fetch(
          `${process.env.VITE_API_URL}/api/projects?id=${projectId}`,
          {
            credentials: 'include',
            cache: 'no-cache',
            signal: ac.signal
          }
        )
        if (!projRes.ok || ac.signal.aborted) {
          return
        }
        const projectData = await projRes.json()
        if (!projectData?._id || projectData._id !== projectId || ac.signal.aborted) {
          return
        }

        useConfigStore.getState().resetLoadingProgress()
        setConfig(projectData)
        setStoredProjectEffectiveUpdatedAt(projectId, effectiveUpdatedAt)
        await refreshProjectCatalog(projectId)
      } catch (e) {
        if ((e as Error).name === 'AbortError') {
          return
        }
        console.warn('[VIEW] Welcome project refresh failed:', e)
      }
    }

    void run()
    return (): void => ac.abort()
  }, [projectId, user, isRootPath, sessionId, setConfig, refreshProjectCatalog])

  const uploadQueueSize = uploadProgress.pending + uploadProgress.uploading
  // Calculate effective screensaver timeout: use project's idleTimeout if set, otherwise fall back to local screensaverTimeout setting
  // Use || instead of ?? to treat 0 as falsy and ensure a minimum of 10 seconds
  const effectiveScreensaverTimeout = Math.max(config?.theme?.idleTimeout || screensaverTimeout, 10)
  const hasScreensaverVideo = Boolean(config?.theme?.screensaverVideoUrl)
  // Use project's enableScreensaver setting if defined, otherwise fall back to local setting
  const effectiveEnableScreensaver = config?.theme?.enableScreensaver ?? enableScreensaver

  // Handler for settings visibility
  const handleSettingsVisibility = (show: boolean, fromGear = false): void => {
    setShowSettings(show)
    setIsFromGearButton(fromGear)
    if (!show) {
      setIsFromGearButton(false)
    }
  }

  const handleCameraConnect = async (): Promise<void> => {
    
    if (selectedCamera) {
      try {
        await ensureCameraPermission()
        let deviceType: DeviceType = 'webcam'
        let params: string | { productId?: number; vendorId?: number } = selectedCamera

        if (!isNaN(Number(selectedCamera))) {
          deviceType = 'camera'
          params = { productId: Number(selectedCamera) }
        } else if (selectedCamera === 'detect') {
          deviceType = 'camera'
          params = {}
        }
        const connectPromise = unifiedCameraUtils.tryToConnect(deviceType, params, 'primary')
        const timeoutPromise = new Promise((_resolve, reject) => {
          setTimeout(() => {
            reject(new Error('Camera connection timed out'))
          }, 5000)
        })
        await Promise.race([connectPromise, timeoutPromise])

        // Verify camera actually works after connection (avoid stale/faulty session)
        const healthCheck = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheck.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }

        setShowCameraPrompt(false)

        reconnectFailureCount.current = 0

        // Increment connection key to force Preview remount in Welcome page
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        // If this was a recovery reconnection, call the callback
        if (cameraRecoveryCallbackRef.current) {
          console.log('[VIEW] Camera reconnected from recovery, calling callback')
          cameraRecoveryCallbackRef.current()
          cameraRecoveryCallbackRef.current = null
        }

        // Reset idle timer immediately after successful camera reconnection
        // This handles the case where user reconnects and then leaves
        // Don't start timer if settings modal or camera prompt is open
        const currentPage = location.pathname.split('/').pop() || 'welcome'
        const isWelcomePage = isRootPath || currentPage === 'welcome'
        const screensaverHandlesIdle = effectiveEnableScreensaver && hasScreensaverVideo
        if (
          isWelcomePage &&
          ejectCameraWhenIdle &&
          !isScreensaverActive &&
          !screensaverHandlesIdle &&
          !showSettings &&
          !showCameraPrompt
        ) {
          // Clear existing timer
          if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current)
            idleTimerRef.current = null
          }
          // Start new timer immediately
          console.log('[VIEW] Camera reconnected, restarting idle timer')
          idleTimerRef.current = setTimeout(async () => {
            console.log('[VIEW] Idle timeout reached, ejecting camera')
            try {
              await unifiedCameraUtils.disconnect('primary')
              const cameraStore = useCameraStore.getState()
              if (
                !cameraStore.camera &&
                !cameraStore.webcam &&
                !showSettings &&
                !showCameraPrompt
              ) {
                console.log('[VIEW] Camera disconnected from idle, showing prompt immediately')
                setShowCameraPrompt(true)
              }
            } catch (error) {
              console.error('[VIEW] Error disconnecting camera on idle:', error)
            }
            idleTimerRef.current = null
          }, 60000) // 60 seconds
        }
      } catch (cameraError) {
        console.error('Failed to connect to camera:', cameraError)
        const errorMessage = (cameraError as Error).message
        reconnectFailureCount.current++

        // If this was a recovery reconnection attempt, allow one retry then escalate
        if (cameraRecoveryCallbackRef.current) {
          if (reconnectFailureCount.current >= 2) {
            console.log('[VIEW] Recovery reconnection failed repeatedly, escalating to SettingsModal')
            cameraRecoveryCallbackRef.current = null
            reconnectFailureCount.current = 0
            const { setSelectedCamera } = useSettingsStore.getState()
            setSelectedCamera(undefined)
            setShowCameraPrompt(false)
            setShowSettings(true)
            cogoToast.error(
              'Camera connection failed repeatedly. Please re-select your camera.',
              { hideAfter: 5 }
            )
          } else {
            console.log('[VIEW] Recovery reconnection failed, keeping CameraPrompt open for retry')
            cogoToast.error(`Failed to connect: ${errorMessage}. Please try again.`, { hideAfter: 5 })
          }
          return
        }

        // For non-recovery connections, if CameraPrompt fails, clear selectedCamera and show settings
        console.log('[VIEW] CameraPrompt failed, clearing selectedCamera and showing SettingsModal')
        const { setSelectedCamera } = useSettingsStore.getState()
        setSelectedCamera(undefined)
        setShowCameraPrompt(false)
        setShowSettings(true)
        cogoToast.error(`Failed to connect to camera: ${errorMessage}`)
      }
    } else {
      // No camera selected - close prompt and show settings
      console.log('[VIEW] No camera selected, showing SettingsModal')
      setShowCameraPrompt(false)
      setShowSettings(true)
      // Clear recovery callback if no camera selected
      if (cameraRecoveryCallbackRef.current) {
        console.log('[VIEW] No camera selected during recovery, clearing callback')
        cameraRecoveryCallbackRef.current = null
      }
    }
  }

  // Camera recovery request handler
  const handleCameraRecoveryRequest = (onReconnected: () => void): void => {
    console.log('[VIEW] Camera recovery requested, showing CameraPrompt')
    cameraRecoveryCallbackRef.current = onReconnected
    setShowCameraPrompt(true)
  }

  // Effects
  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        if (!projectId) throw new Error('No project ID provided')
        await initializeSession({ projectId, user })
        await fetchSubscriptionAndCredits() // Fetch credits and subscription on mount

        // Send app settings telemetry on startup (lightweight ping, no GPS/PIN)
        import('@renderer/utils/sendAppSettingsTelemetry').then(({ sendAppSettingsTelemetry }) => {
          sendAppSettingsTelemetry({ projectId }).catch(() => {})
        })

        // Initialize asset cache cleanup
        try {
          const { useImageCacheStore } = await import('@renderer/stores/imageCacheStore')
          const imageCacheStore = useImageCacheStore.getState()

          // Clear expired entries on startup
          await imageCacheStore.clearExpired()

          // Set up periodic cleanup (every hour)
          setInterval(
            async () => {
              await imageCacheStore.clearExpired()
            },
            60 * 60 * 1000
          ) // 1 hour
        } catch (error) {
          console.warn('Error initializing asset cache cleanup:', error)
        }
      } catch (error) {
        console.error('Error initializing:', error)
        cogoToast.error(`${(error as Error).message || 'Failed to initialize application'}`, {
          hideAfter: 10
        })
      } finally {
        setIsLoading(false)
        setInitialCreditsLoaded(true)
      }
    }

    initialize()
  }, [projectId, user, initializeSession, fetchSubscriptionAndCredits])

  // Listen for camera validation requests from Welcome page
  useEffect(() => {
    const handleShowCameraPrompt = (): void => {
      if (!showSettings && !showCameraPrompt) {
        setShowCameraPrompt(true)
      }
    }

    const handleShowSettingsModal = (): void => {
      if (!showSettings) {
        setShowSettings(true)
      }
    }

    window.addEventListener('showCameraPrompt', handleShowCameraPrompt)
    window.addEventListener('showSettingsModal', handleShowSettingsModal)

    return (): void => {
      window.removeEventListener('showCameraPrompt', handleShowCameraPrompt)
      window.removeEventListener('showSettingsModal', handleShowSettingsModal)
    }
  }, [showSettings, showCameraPrompt])

  // Device validation on load: when devices are fetched, check stored camera/printer and show CameraPrompt or open SettingsModal
  useEffect(() => {
    if (!devices || initialDeviceValidationDone.current || !config) return
    const isEditCustomPath = customPath === 'edit'

    let openSettings = false

    // Camera: only validate if we need camera (not edit customPath)
    if (!isEditCustomPath && selectedCamera) {
      const webcamFound = devices.cameras.webcams.some((w) => w.deviceId === selectedCamera)
      const usbFound = devices.cameras.usbCameras.some((c) => `${c.productId}` === selectedCamera)
      const isDetect = selectedCamera === 'detect'

      if (isDetect) {
        setShowCameraPrompt(true)
      } else if (webcamFound) {
        unifiedCameraUtils
          .tryToConnect('webcam', selectedCamera, 'primary')
          .then(() => {
            setShowCameraPrompt(false)
            const { incrementCameraConnectionKey } = useSettingsStore.getState()
            incrementCameraConnectionKey()
          })
          .catch(() => {
            cogoToast.warn(
              t('view.deviceValidation.cameraNotFound', 'Previously selected camera could not be connected.')
            )
            setShowSettings(true)
          })
      } else if (usbFound) {
        setShowCameraPrompt(true)
      } else {
        cogoToast.warn(
          t('view.deviceValidation.cameraNotFound', 'Previously selected camera not found.')
        )
        openSettings = true
      }
    }

    // Printer: warn and open settings if printing enabled but stored printer not in list
    if (
      isPrintingEnabled &&
      selectedPrinter &&
      devices.printers?.length !== undefined &&
      !devices.printers.includes(selectedPrinter)
    ) {
      cogoToast.warn(
        t('view.deviceValidation.printerNotFound', 'Previously selected printer not found.')
      )
      openSettings = true
    }

    if (openSettings) {
      setShowSettings(true)
    }
    initialDeviceValidationDone.current = true
  }, [devices, config, customPath, selectedCamera, selectedPrinter, isPrintingEnabled, t])

  const isElectron = hasElectronPreload()

  // Fetch tunnel status in Electron environment
  useEffect(() => {
    if (!isElectron) return
    const fetchTunnel = async (): Promise<void> => {
      try {
        const status = await getTunnelStatus()
        setTunnelUrl(status.url)
        setIsTunnelActive(status.isActive)
      } catch (error) {
        console.error('Failed to fetch tunnel status:', error)
        setTunnelUrl(null)
        setIsTunnelActive(false)
      }
    }
    void fetchTunnel()
  }, [isElectron])

  // Auto POST to /api/remote-printer when tunnel is active and settings/tunnelUrl change
  useEffect(() => {
    if (isElectron && tunnelUrl && isTunnelActive) {
      const postRemotePrinter = async (): Promise<void> => {
        try {
          const printSettings = {
            ...settings,
            margins: {
              marginType: settings.margins?.marginType || 'default',
              ...(settings.margins?.marginType === 'custom' ? settings.margins : {})
            },
            dpi: settings.dpi || { horizontal: 600, vertical: 600 },
            scaleFactor: settings.scaleFactor || 100,
            printBackground: settings.printBackground ?? true,
            printResolutionScale: settings.printResolutionScale || 1
          }
          await fetch(`${process.env.VITE_API_URL}/api/remote-printer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              tunnelUrl,
              printSettings,
              projectId,
              printerName: selectedPrinter || 'DS-RX1'
            })
          })
        } catch (err) {
          console.error('Failed to register remote printer from View:', err)
        }
      }
      // Register immediately, then heartbeat so an idle-but-running booth keeps its
      // registration fresh. The server prunes booths that stop checking in, so without
      // this an idle booth would disappear from the web /strip dropdown after the TTL.
      postRemotePrinter()
      const heartbeat = setInterval(postRemotePrinter, 60_000)
      return () => clearInterval(heartbeat)
    }
    return undefined
  }, [isElectron, tunnelUrl, isTunnelActive, settings, selectedPrinter])

  // Monitor camera status and image quality
  useEffect(() => {
    let isMonitoring = true
    // Require multiple CONSECUTIVE non-responding polls before tearing the camera down.
    // A single failed liveview read on a USB-tethered DSLR is usually a transient hiccup
    // (or momentary contention), not a disconnect — nulling on the first miss is what
    // surfaced a CameraPrompt mid-session and stranded paid customers. A genuine unplug
    // keeps failing and crosses the threshold within ~10s.
    let consecutiveNotResponding = 0
    const MONITOR_NOT_RESPONDING_THRESHOLD = 2
    const monitorCamera = async (): Promise<void> => {
      while (isMonitoring) {
        // Skip camera monitoring for edit customPath
        if (customPath === 'edit') {
          await new Promise((resolve) => setTimeout(resolve, 5000))
          continue
        }

        // For cesterstudio@gmail.com: Skip preview capture calls entirely
        // This is to test behavior without keep-alive mechanism
        const shouldSkipPreviewCapture = user?.email === 'cesterstudio@gmail.com'

        // Check both selectedCamera and actual camera/webcam state
        const cameraStore = useCameraStore.getState()
        const hasSelectedCamera = !!selectedCamera
        const hasActiveCamera = !!(cameraStore.camera || cameraStore.webcam)

        // Only monitor on pages where the camera is actively needed. Result/Edit/Filter/Select/Checkout/Payment/Frame/Retake
        // do not need a live preview — surfacing a CameraPrompt there hijacks pages like Result mid-QR-scan. Record has its
        // own dedicated preview pipeline and is intentionally excluded.
        const currentPage = location.pathname.split('/').pop() || 'welcome'
        const isCaptureRelatedPage =
          currentPage === 'welcome' ||
          currentPage === projectId || // root path /:projectId
          currentPage === 'tutorial' ||
          currentPage === 'camera' ||
          currentPage === 'capture' ||
          currentPage === 'retake' // user may re-shoot — camera still needs to be alive

        // Only monitor if we have a selected camera OR an active camera connection
        // But also check if selectedCamera exists but camera is disconnected (stale state)
        if (
          isCaptureRelatedPage &&
          (hasSelectedCamera || hasActiveCamera) &&
          !isCameraWarningIgnored && // Skip quality checks if warning is ignored
          !shouldSkipPreviewCapture // Skip for cesterstudio@gmail.com
        ) {
          const qualityResult = await unifiedCameraUtils.isDeviceResponding('primary')

          if (!qualityResult.isResponding) {
            consecutiveNotResponding += 1
            // Only clear the camera + prompt after sustained unresponsiveness — not a single blip.
            if (
              consecutiveNotResponding >= MONITOR_NOT_RESPONDING_THRESHOLD &&
              !showSettings &&
              !showCameraPrompt
            ) {
              console.log(
                `[VIEW] Camera not responding ${consecutiveNotResponding}x, clearing camera state and showing prompt`
              )
              useCameraStore.getState().setCamera(null)
              useCameraStore.getState().setWebcam(null)
              // Keep selectedCamera so CameraPrompt knows which camera to reconnect
              setShowCameraPrompt(true)
            }
          } else {
            // Healthy frame — reset the streak.
            consecutiveNotResponding = 0
            if (
              !qualityResult.isImageValid &&
              !showCameraWarning &&
              showCameraQualityWarnings // Only show warning if setting is enabled
            ) {
              // Show warning for image quality issues
              setCameraQualityIssue(qualityResult.qualityIssue || 'none')
              setShowCameraWarning(true)
            }
          }
        } else {
          // Not actively monitoring this tick — don't carry a stale failure streak forward.
          consecutiveNotResponding = 0
        }
        await new Promise((resolve) => setTimeout(resolve, 5000))
      }
    }

    monitorCamera()

    return (): void => {
      isMonitoring = false
    }
  }, [
    location.pathname,
    projectId,
    selectedCamera,
    showSettings,
    showCameraPrompt,
    showCameraWarning,
    isCameraWarningIgnored,
    customPath,
    user?.email,
    showCameraQualityWarnings
  ])

  // Idle detection for camera ejection on welcome page
  useEffect(() => {
    const currentPage = location.pathname.split('/').pop() || 'welcome'
    const isWelcomePage = isRootPath || currentPage === 'welcome'
    const hasActiveCamera = !!(camera || webcam)

    // Only run idle detection on welcome page when camera is connected and feature is enabled
    // Don't run if settings modal or camera prompt is open
    if (
      !isWelcomePage ||
      !ejectCameraWhenIdle ||
      !hasActiveCamera ||
      showSettings ||
      showCameraPrompt ||
      isScreensaverActive ||
      (effectiveEnableScreensaver && hasScreensaverVideo)
    ) {
      // Clear timer if we're not on welcome page, feature is disabled, or modals are open
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
      return
    }

    // Reset timer function
    const resetIdleTimer = (): void => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }

      // Start new 60-second timer
      idleTimerRef.current = setTimeout(async () => {
        console.log('[VIEW] Idle timeout reached, ejecting camera')
        try {
          await unifiedCameraUtils.disconnect('primary')
          // Keep selectedCamera so CameraPrompt knows which camera to reconnect
          // Immediately check camera state and show prompt if needed
          const cameraStore = useCameraStore.getState()
          if (!cameraStore.camera && !cameraStore.webcam && !showSettings && !showCameraPrompt) {
            console.log('[VIEW] Camera disconnected from idle, showing prompt immediately')
            setShowCameraPrompt(true)
          }
        } catch (error) {
          console.error('[VIEW] Error disconnecting camera on idle:', error)
        }
        idleTimerRef.current = null
      }, 60000) // 60 seconds
    }

    // Start timer when entering welcome page with camera connected
    resetIdleTimer()

    // Listen for clicks to reset timer
    const handleClick = (): void => {
      // Only reset if still on welcome page, camera still connected, and modals are not open
      const currentCameraStore = useCameraStore.getState()
      const stillOnWelcome =
        isRootPath || (location.pathname.split('/').pop() || 'welcome') === 'welcome'
      if (
        stillOnWelcome &&
        ejectCameraWhenIdle &&
        (currentCameraStore.camera || currentCameraStore.webcam) &&
        !showSettings &&
        !showCameraPrompt
      ) {
        resetIdleTimer()
      }
    }

    document.addEventListener('click', handleClick)
    document.addEventListener('touchstart', handleClick)
    document.addEventListener('mousemove', handleClick)
    document.addEventListener('keydown', handleClick)

    // Cleanup
    return (): void => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
      document.removeEventListener('click', handleClick)
      document.removeEventListener('touchstart', handleClick)
      document.removeEventListener('mousemove', handleClick)
      document.removeEventListener('keydown', handleClick)
    }
  }, [
    location.pathname,
    isRootPath,
    ejectCameraWhenIdle,
    selectedCamera,
    camera,
    webcam,
    showSettings,
    showCameraPrompt,
    isScreensaverActive,
    effectiveEnableScreensaver,
    hasScreensaverVideo
  ])

  // Screensaver idle detection (Welcome page only)
  useEffect(() => {
    // Debug logging
    console.log('[VIEW] Screensaver effect check:', { 
      effectiveEnableScreensaver,
      projectEnableScreensaver: config?.theme?.enableScreensaver,
      localEnableScreensaver: enableScreensaver,
      hasScreensaverVideo, 
      screensaverVideoUrl: config?.theme?.screensaverVideoUrl,
      showSettings, 
      showCameraPrompt,
      effectiveScreensaverTimeout 
    })
    
    const currentPage = location.pathname.split('/').pop() || 'welcome'
    const isWelcomePage = isRootPath || currentPage === 'welcome'

    // Only run this if on welcome page, screensaver is enabled, video exists, and settings/modals are not open
    if (
      !isWelcomePage ||
      !effectiveEnableScreensaver ||
      !hasScreensaverVideo ||
      showSettings ||
      showCameraPrompt
    ) {
      if (screensaverTimerRef.current) {
        clearTimeout(screensaverTimerRef.current)
        screensaverTimerRef.current = null
      }
      return
    }

    const resetScreensaverTimer = (): void => {
      if (screensaverTimerRef.current) {
        clearTimeout(screensaverTimerRef.current)
        screensaverTimerRef.current = null
      }
      
      const { isIdle: currentIdle } = useIdleStore.getState()
      if (currentIdle) return

      screensaverTimerRef.current = setTimeout(() => {
        console.log('[VIEW] Screensaver timeout reached, activating screensaver')
        setIdle(true)
        setScreensaverActive(true)
        void setIdleState(true)

        screensaverTimerRef.current = null
      }, effectiveScreensaverTimeout * 1000)
    }

    resetScreensaverTimer()

    const handleInteraction = (): void => {
      const { isIdle: currentIdle } = useIdleStore.getState()
      
      // Wake up first if idle, then reset timer (order matters!)
      if (currentIdle && !showSettings && !showCameraPrompt) {
        console.log('[VIEW] User interaction detected, waking up from screensaver')
        wakeUp()
        void setIdleState(false)
      }
      
      // Reset timer after wakeUp so isIdle is false and timer will be set
      resetScreensaverTimer()
    }

    document.addEventListener('click', handleInteraction)
    document.addEventListener('touchstart', handleInteraction)
    document.addEventListener('mousemove', handleInteraction)
    document.addEventListener('keydown', handleInteraction)

    return (): void => {
      if (screensaverTimerRef.current) {
        clearTimeout(screensaverTimerRef.current)
        screensaverTimerRef.current = null
      }
      document.removeEventListener('click', handleInteraction)
      document.removeEventListener('touchstart', handleInteraction)
      document.removeEventListener('mousemove', handleInteraction)
      document.removeEventListener('keydown', handleInteraction)
    }
  }, [
    location.pathname,
    isRootPath,
    effectiveEnableScreensaver,
    hasScreensaverVideo,
    effectiveScreensaverTimeout,
    showSettings,
    showCameraPrompt,
    setIdle,
    setScreensaverActive,
    wakeUp
  ])

  useEffect(() => {
    const pageWhenEffectRan = location.pathname.split('/').pop() || 'welcome'
    const handleClick = (e: MouseEvent): void => {
      const element = e.target as HTMLElement
      const buttonElement = element.closest('button')
      const interactiveElement = element.closest(
        'button, a, [role="button"], [role="option"]'
      ) as HTMLElement
      const isOperatorArea = element.closest('[data-operator-area]')

      if (interactiveElement && !isOperatorArea) {
        if (
          interactiveElement.hasAttribute('data-button-action') ||
          interactiveElement.hasAttribute('data-automated-click')
        ) {
          return
        }
        const timestamp = new Date()
        lastClickRef.current = {
          element: interactiveElement,
          timestamp
        }

        const actionType = interactiveElement.getAttribute('role') === 'option' ? 'change' : 'click'

        const logElement =
          buttonElement?.title || interactiveElement.getAttribute('aria-label') || undefined
        const text = interactiveElement.textContent?.trim()

        let logMessage: string
        if (actionType === 'click') {
          logMessage = `User clicked on "${logElement || text}" button`
        } else if (actionType === 'change') {
          logMessage = `User selected "${logElement || text}" option`
        } else {
          logMessage = `User performed ${actionType} on "${logElement || text} element"`
        }

        const navTo = interactiveElement.getAttribute('data-nav-to')

        logActivity(
          logMessage,
          {
            element: logElement,
            text,
            role: interactiveElement.getAttribute('role') || 'button',
            ...(navTo && { to: navTo })
          },
          navTo ? undefined : pageWhenEffectRan
        )
      }
    }

    document.addEventListener('click', handleClick)
    return (): void => document.removeEventListener('click', handleClick)
  }, [location.pathname])

  useEffect(() => {
    if (printStatus === 'error' && printError) {
      cogoToast.error(`Print error: ${printError}`)
    } else if (printStatus === 'success') {
      cogoToast.success('Print completed successfully')
    }
  }, [printStatus, printError])

  // Reset dismiss state when indicators would naturally hide so they show again next time
  useEffect(() => {
    if (loadingProgress.loaded >= loadingProgress.total && loadingProgress.total > 0) {
      setDismissedLoadingIndicator(false)
    }
  }, [loadingProgress.loaded, loadingProgress.total])
  useEffect(() => {
    if (uploadQueueSize === 0) {
      setDismissedUploadIndicator(false)
    }
  }, [uploadQueueSize])

  useEffect(() => {
    // Check if this is a Bahela project that should bypass credits
    const welcomeSettings = getPageSettings('welcome')
    const customPath = welcomeSettings?.customPath
    const isBahelaCustomPath = customPath && ['bahela-event', 'bahela'].includes(customPath)
    const isBahelaHost = window.location.hostname === 'app.bahela.id'
    const isEditCustomPath = customPath === 'edit'

  // Skip credits check for Bahela projects, hosts, or edit customPath
  if (isBahelaCustomPath || isBahelaHost || isEditCustomPath) {
    setShowNoCreditsModal(false)
    setShowUpgradeModal(false)
    return
  }

  // If subscription is invalid and no usable credits, show NoCreditsModal immediately
  if (initialCreditsLoaded && !hasUsableBoothAccess(isSubscriptionValid, credits)) {
    setShowNoCreditsModal(true)
    return
  }

  // If device mismatch error, show UpgradeModal directly (user can change device)
    if (initialCreditsLoaded && validationError === 'device_mismatch') {
      setShowUpgradeModal(true)
      setShowNoCreditsModal(false)
      return
    }

    // For other validation errors or no subscription, show NoCreditsModal if no access
    if (initialCreditsLoaded && !hasUsableBoothAccess(isSubscriptionValid, credits)) {
      setShowNoCreditsModal(true)
      // Only show UpgradeModal if it's not a device mismatch (already handled above)
      if (validationError && validationError !== 'device_mismatch') {
        setShowUpgradeModal(false)
      }
    } else {
      if (noCreditsModalTimerRef.current) {
        clearTimeout(noCreditsModalTimerRef.current)
        noCreditsModalTimerRef.current = null
      }
      setShowNoCreditsModal(false)
      // Don't auto-close UpgradeModal - let user close it manually
    }
  }, [isSubscriptionValid, credits, initialCreditsLoaded, validationError])

  // When session upload fails with non-transient 403 (e.g. Insufficient credits), re-validate subscription so NoCreditsModal can show.
  // Surface the failure to the operator/customer via a toast + structured log; debounce per (session, reason) so retries don't spam.
  const sessionUploadToastDedupeRef = useRef<Map<string, number>>(new Map())
  useEffect(() => {
    if (!sessionUploadError) return
    const raw = sessionUploadError
    setSessionUploadError(null)

    // Classify the underlying reason from the NON_TRANSIENT message thrown by stateUploader.
    const reason: 'insufficient_credits' | 'device_mismatch' | 'forbidden' | 'unknown' =
      /Insufficient credits/i.test(raw)
        ? 'insufficient_credits'
        : /Device not authorized/i.test(raw)
          ? 'device_mismatch'
          : /Forbidden/i.test(raw)
            ? 'forbidden'
            : 'unknown'

    console.warn('[SESSION UPLOAD] Non-transient failure', {
      reason,
      raw,
      sessionId,
      projectId
    })

    // 60s dedupe per (sessionId, reason) so a backend that keeps rejecting doesn't flood the screen.
    const dedupeKey = `${sessionId ?? '_'}|${reason}`
    const now = Date.now()
    const last = sessionUploadToastDedupeRef.current.get(dedupeKey) ?? 0
    if (now - last >= 60_000) {
      sessionUploadToastDedupeRef.current.set(dedupeKey, now)
      const message =
        reason === 'insufficient_credits'
          ? t(
              'view.sessionUploadError.insufficientCredits',
              'Session not saved: insufficient credits. Please contact the operator.'
            )
          : reason === 'device_mismatch'
            ? t(
                'view.sessionUploadError.deviceMismatch',
                'Session not saved: this device is not authorized for the active subscription.'
              )
            : t(
                'view.sessionUploadError.generic',
                'Session could not be saved. Please contact the operator.'
              )
      cogoToast.error(message, { hideAfter: 8 })
    }

    fetchSubscriptionAndCredits().catch(() => {})
  }, [sessionUploadError, setSessionUploadError, fetchSubscriptionAndCredits, sessionId, projectId, t])

  if (!config || isLoading) {
    return <Loading />
  }

  const cameraRecoveryContextValue: CameraRecoveryContextType = {
    onCameraRecoveryRequest: handleCameraRecoveryRequest
  }

  const sessionBackgroundColor = config.theme?.backgroundColor || 'white'

  const welcomeRouteElement = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <pageComponents.Welcome />
    </div>
  )

  const sessionChromeBody = (
    <>
      <div className="relative flex h-full min-h-0 w-full flex-col">
        <ControlButtons
          isRootPath={isRootPath}
          setShowSettings={handleSettingsVisibility}
          hideExitButton={Boolean(customPath)}
        />

        <div className="relative flex min-h-0 flex-1 flex-col">
          <Routes location={location}>
            <Route path="/" element={welcomeRouteElement} />
            <Route path="welcome" element={<Navigate to="/" />} />
            {pageComponents.Tutorial && (
              <Route
                path="tutorial"
                element={
                  <PageWrapper>
                    <pageComponents.Tutorial />
                  </PageWrapper>
                }
              />
            )}
            <Route
              path="camera"
              element={
                <PageWrapper>
                  <pageComponents.Camera />
                </PageWrapper>
              }
            />
            <Route
              path="payment"
              element={
                <PageWrapper>
                  <pageComponents.Payment />
                </PageWrapper>
              }
            />
            <Route
              path="filter"
              element={
                <PageWrapper>
                  <pageComponents.Filter />
                </PageWrapper>
              }
            />
            <Route
              path="frame"
              element={
                <PageWrapper>
                  <pageComponents.Frame />
                </PageWrapper>
              }
            />
            <Route
              path="capture"
              element={
                <PageWrapper>
                  <pageComponents.Capture />
                </PageWrapper>
              }
            />
            <Route
              path="record"
              element={
                <PageWrapper>
                  <RecordPage />
                </PageWrapper>
              }
            />
            <Route
              path="post-session-ai"
              element={
                <PageWrapper>
                  <PostSessionAIPage />
                </PageWrapper>
              }
            />
            <Route
              path="result"
              element={
                <PageWrapper>
                  <pageComponents.Result />
                </PageWrapper>
              }
            />
            <Route
              path="retake"
              element={
                <PageWrapper>
                  <pageComponents.Retake />
                </PageWrapper>
              }
            />
            {pageComponents.Processing && (
              <Route
                path="processing"
                element={
                  <PageWrapper>
                    <pageComponents.Processing />
                  </PageWrapper>
                }
              />
            )}
            <Route
              path="select"
              element={
                <PageWrapper>
                  <pageComponents.Select />
                </PageWrapper>
              }
            />
            <Route
              path="edit"
              element={
                <PageWrapper>
                  <pageComponents.Edit />
                </PageWrapper>
              }
            />
            <Route
              path="checkout"
              element={
                <PageWrapper>
                  <pageComponents.Checkout />
                </PageWrapper>
              }
            />
            <Route path="*" element={<Navigate to={`/${projectId}`} replace />} />
          </Routes>
        </div>
      </div>

      {showSettings && (
        <SettingsModal
          setShowSettings={handleSettingsVisibility}
          isFromGearButton={isFromGearButton}
          height={scaleToFitScreenSize?.height ?? config.screenSize.height}
          devices={devices}
          fetchDevices={fetchDevices}
          cameraConnectionRef={cameraConnectionRef}
          isPreviewAvailable={isPreviewAvailable}
          setIsPreviewAvailable={setIsPreviewAvailable}
          localTempCamera={localTempCamera}
          setLocalTempCamera={setLocalTempCamera}
        />
      )}
      {uploadQueueSize > 0 && !dismissedUploadIndicator && (
        <div className="absolute bottom-4 left-4 flex items-center gap-1 bg-gray-900 bg-opacity-50 text-white text-sm pl-2 pr-1 py-1 rounded">
          <span>Uploading {uploadQueueSize} media...</span>
          <button
            type="button"
            onClick={() => setDismissedUploadIndicator(true)}
            className="p-0.5 rounded hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-1 focus:ring-white"
            aria-label={t('view.dismissUploadIndicator', 'Dismiss upload indicator')}
          >
            <FaTimes className="w-3 h-3" />
          </button>
        </div>
      )}
      {loadingProgress.total > 0 &&
        loadingProgress.loaded < loadingProgress.total &&
        uploadQueueSize === 0 &&
        !dismissedLoadingIndicator && (
          <div className="absolute bottom-4 left-4 flex items-center gap-1 bg-gray-900 bg-opacity-50 text-white text-sm pl-2 pr-1 py-1 rounded">
            <span>
              Loading assets... {loadingProgress.loaded} / {loadingProgress.total}
            </span>
            <button
              type="button"
              onClick={() => setDismissedLoadingIndicator(true)}
              className="p-0.5 rounded hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-1 focus:ring-white"
              aria-label={t('view.dismissLoadingIndicator', 'Dismiss loading indicator')}
            >
              <FaTimes className="w-3 h-3" />
            </button>
          </div>
        )}
      {showCameraPrompt && <CameraPrompt onStart={handleCameraConnect} />}
      {showCameraWarning && (
        <CameraWarningModal
          isVisible={showCameraWarning}
          qualityIssue={cameraQualityIssue}
          onClose={() => setShowCameraWarning(false)}
          onRetry={async () => {
            setShowCameraWarning(false)
            try {
              const qualityResult = await unifiedCameraUtils.isDeviceResponding('primary')
              if (qualityResult.isResponding && qualityResult.isImageValid) {
                return
              } else if (qualityResult.isResponding && !qualityResult.isImageValid) {
                setCameraQualityIssue(qualityResult.qualityIssue || 'none')
                setShowCameraWarning(true)
              }
            } catch (error) {
              console.error('Error retrying camera check:', error)
            }
          }}
          onIgnore={() => {
            setShowCameraWarning(false)
            setIsCameraWarningIgnored(true)
          }}
        />
      )}
      {showNoCreditsModal && (
        <NoCreditsModal
          onClose={() => {
            navigate('/')
            setDefaultProjectId(undefined)
          }}
          setShowUpgradeModal={setShowUpgradeModal}
        />
      )}
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
      <PoweredByWatermark customPath={customPath ?? ''} />
    </>
  )

  const sessionChrome = (
    <ResponsiveTierProvider virtualViewport={scaleToFitVirtualViewport}>
      {useScaleToFitShell ? (
        <div className="relative h-full min-h-0 w-full min-w-0 flex-1 overflow-hidden bg-black">
          <ScaleToFit
            screenSize={scaleToFitScreenSize ?? config.screenSize}
            backgroundColor="white"
            blurBackgroundColor="#1D1D1D"
          >
            {sessionChromeBody}
          </ScaleToFit>
        </div>
      ) : (
        sessionChromeBody
      )}
    </ResponsiveTierProvider>
  )

  return (
    <CameraRecoveryContext.Provider value={cameraRecoveryContextValue}>
      <div className="w-screen overflow-hidden" style={{ height: '100dvh' }}>
        <div
          className="relative flex h-full min-h-0 w-full flex-col"
          style={{
            backgroundColor: useScaleToFitShell ? '#1D1D1D' : sessionBackgroundColor
          }}
        >
          {sessionChrome}
        </div>
        <Screensaver />
      </div>
    </CameraRecoveryContext.Provider>
  )
}

export default React.memo(View) // Memoize the entire View component
